import produce from 'immer';
import { CartState } from './cart-state-models';
import { CartActionTypes } from './cart.actions';

export const initialState: CartState = {
  loading: false,
  addSurpriseBuyLoading: false,
  cart: null,
  modals: {
    addToCartConfirm: {
      loading: false,
      data: null,
    },
    quantityLimitErrorModal: {
      data: null,
    },
    purchaseLimitRuleModals: {
      simpleModal: {
        data: null,
        product: null,
      },
      multipleLineItemModal: {
        shouldOpenModal: false,
        items: null,
      },
      isCartEmpty: false,
    },
  },
  quantityLimitsMap: {},
};
export const reducer = produce((draft, action) => {
  switch (action.type) {
    case CartActionTypes.resetCartState:
    case CartActionTypes.createCart:
      return initialState;

    case CartActionTypes.fetchCart:
      draft.loading = true;
      return draft;

    case CartActionTypes.fetchCartSuccess:
      draft.cart = action.cart;
      draft.loading = false;
      return draft;

    case CartActionTypes.fetchCartFailure:
      draft.loading = false;
      return draft;

    case CartActionTypes.updateOrderLine:
      draft.loading = true;
      return;

    case CartActionTypes.updateOrderLineSuccess:
      draft.cart = action.cart;
      draft.loading = false;
      return draft;

    case CartActionTypes.updateOrderLineFailure:
      draft.loading = false;
      if (action.orderLineId != null && action.actualCurrentQty != null) {
        draft.cart.orderLines = draft.cart.orderLines.map((line) =>
          line.orderLineId === action.orderLineId
            ? { ...line, quantity: action.actualCurrentQty }
            : line,
        );
      }
      return;

    case CartActionTypes.addToCart:
      draft.modals.addToCartConfirm.data = null;
      draft.modals.addToCartConfirm.loading = true;
      draft.modals.purchaseLimitRuleModals.simpleModal.product = action.payload;
      return draft;

    case CartActionTypes.addToCartSuccess:
      draft.modals.addToCartConfirm.data = action.payload;
      draft.modals.addToCartConfirm.loading = false;
      draft.modals.purchaseLimitRuleModals.simpleModal =
        initialState.modals.purchaseLimitRuleModals.simpleModal;
      return draft;

    case CartActionTypes.addToCartFailure:
      draft.modals.addToCartConfirm.loading = false;
      draft.modals.purchaseLimitRuleModals.simpleModal =
        initialState.modals.purchaseLimitRuleModals.simpleModal;
      return draft;

    case CartActionTypes.deleteFromCart:
      draft.loading = true;
      return;

    case CartActionTypes.deleteFromCartSuccess:
      draft.cart = action.cart;
      draft.loading = false;
      return draft;

    case CartActionTypes.deleteFromCartFailure:
      draft.loading = false;
      return;

    case CartActionTypes.setAddToCartConfirmModal:
      draft.modals.addToCartConfirm.data = action.payload;
      draft.modals.addToCartConfirm.loading = false;
      return draft;

    case CartActionTypes.fetchProductsRecommendationsSuccess:
      draft.cart.recommendations = action.recommendations;
      return;

    case CartActionTypes.addSurpriseBuy:
      draft.addSurpriseBuyLoading = true;
      return;

    case CartActionTypes.addSurpriseBuySuccess:
      draft.cart = action.cart;
      draft.addSurpriseBuyLoading = false;
      return draft;

    case CartActionTypes.addSurpriseBuyFailure:
      draft.addSurpriseBuyLoading = false;
      return draft;

    case CartActionTypes.setQuantityLimitErrorModal:
      draft.loading = false;
      draft.modals.addToCartConfirm.loading = false;
      draft.modals.quantityLimitErrorModal.data = action.payload;
      return draft;

    case CartActionTypes.resetQuantityLimitErrorModal:
      draft.modals.quantityLimitErrorModal = initialState.modals.quantityLimitErrorModal;
      return draft;

    case CartActionTypes.revertOrderLineQty:
      draft.cart.orderLines = draft.cart.orderLines.map((line) =>
        line.orderLineId === action.orderLineId ? { ...line, quantity: action.actualQty } : line,
      );
    case CartActionTypes.setOrderTotalLimitError:
      draft.loading = false;
      draft.modals.addToCartConfirm.loading = false;
      draft.modals.quantityLimitErrorModal.data = action.payload;
      return draft;

    case CartActionTypes.patchOrderLineLimits:
      if (draft.cart?.orderLines) {
        action.limits.forEach((limit) => {
          draft.cart.orderLines
            .filter((line) => line.sku === limit.sku)
            .forEach((line) => {
              line.quantityLimit = limit.quantityLimit;
            });
        });
      }

    case CartActionTypes.setPurchaseLimitRuleModal:
      draft.loading = false;
      draft.modals.addToCartConfirm.loading = false;
      draft.modals.purchaseLimitRuleModals.simpleModal.data = action.payload;
      draft.modals.purchaseLimitRuleModals.simpleModal.product = action.product;
      draft.modals.purchaseLimitRuleModals.isCartEmpty = action.isCartEmpty;
      return draft;

    case CartActionTypes.resetPurchaseLimitRuleModal:
      draft.modals.purchaseLimitRuleModals.simpleModal =
        initialState.modals.purchaseLimitRuleModals.simpleModal;
      draft.modals.purchaseLimitRuleModals.isCartEmpty =
        initialState.modals.purchaseLimitRuleModals.isCartEmpty;
      return draft;

    case CartActionTypes.setMultiplePurchaseLimitRuleItems:
      draft.modals.purchaseLimitRuleModals.multipleLineItemModal.items = action.payload.items;
      draft.modals.purchaseLimitRuleModals.isCartEmpty = action.payload.isCartEmpty;
      draft.modals.purchaseLimitRuleModals.multipleLineItemModal.shouldOpenModal =
        action.payload.items.nonBackorderItems.length > 0;
      return draft;

    case CartActionTypes.resetMultiplePurchaseLimitRuleModal:
      draft.loading = false;
      draft.modals.addToCartConfirm.loading = false;
      draft.modals.purchaseLimitRuleModals.multipleLineItemModal =
        initialState.modals.purchaseLimitRuleModals.multipleLineItemModal;
      draft.modals.purchaseLimitRuleModals.isCartEmpty =
        initialState.modals.purchaseLimitRuleModals.isCartEmpty;
      return draft;

    case CartActionTypes.setMultiplePurchaseLimitModalOpen:
      draft.modals.purchaseLimitRuleModals.multipleLineItemModal.shouldOpenModal =
        action.shouldOpenModal;
      return draft;

    case CartActionTypes.revertOrderLineQty:
      draft.cart.orderLines = draft.cart.orderLines.map((line) =>
        line.orderLineId === action.orderLineId ? { ...line, quantity: action.actualQty } : line,
      );
      return draft;

    case CartActionTypes.setOrderTotalLimitError:
      draft.loading = false;
      draft.modals.quantityLimitErrorModal.data = action.payload;
      return draft;

    case CartActionTypes.patchOrderLineLimits:
      action.limits.forEach((limit) => {
        draft.quantityLimitsMap[limit.sku] = limit.quantityLimit;
      });
      return draft;

    default:
      return draft;
  }
}, initialState);
